import * as React from "react";
import { DataTable } from "components/DataTable/DataTable";
import { JobRequestOrigination, JobRequestsService, JobRequestStatus, JobRequestSummary } from "gen/clients";
import { useQuery } from "react-query";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { useHistory } from "react-router-dom";
import { formatDate } from "utils/dateUtils";
import { TokenBasedPaginator } from "components/TokenBasedPaginator/TokenBasedPaginator";
import { JobRequestStatusText } from "../JobRequestStatusText/JobRequestStatusText";
import { FINISHED_JOBREQUEST_STATUSES } from "../../utils/JobRequestUtils";
import Stack from "@mui/material/Stack";
import { useDialogState } from "hooks/useDialogState";
import { DataTableRowAction } from "components/DataTable/types";
import { useSnackbarAlert } from "hooks/useSnackbarAlert";
import { DeleteJobRequestDialog } from "../DeleteJobRequestDialog/DeleteJobRequestDialog";
import { Box, Typography } from "@mui/material";
import { ArchiveJobRequestDialog } from "../ArchiveJobRequestDialog/ArchiveJobRequestDialog";
import {
  DEFAULT_JOB_REQEST_GROUP,
  JobRequestFilter,
  JobRequestFilterSearchParams
} from "../JobRequestFilter/JobRequestFilter";
import { CancelJobRequestDialog } from "../CancelJobRequestDialog/CancelJobRequestDialog";
import { useSessionStorage } from "usehooks-ts";
import Link from "@mui/material/Link";

interface Props {
  origination: JobRequestOrigination;
  getDetailsPagePath: (jobRequestId: string) => string;
  showCompanyName?: boolean;
  hideArchiveAction?: boolean;
  hideDeleteAction?: boolean;
}

interface MTJobRequestsPageSessionState {
  filterParams?: JobRequestFilterSearchParams;
  rowsPerPage?: number;
}

const JobRequestList: React.FC<Props> = ({
  origination,
  getDetailsPagePath,
  showCompanyName,
  hideDeleteAction,
  hideArchiveAction
}) => {
  const history = useHistory();
  const pageSessionStateKeyName = `mtJobRequestsPageStateV1_${origination}`;
  const [selectedJobRequest, setSelectedJobRequest] = React.useState<JobRequestSummary>();
  const [deleteJobRequestDialogOpen, openDeleteJobRequestDialog, closeDeleteJobRequestDialog] = useDialogState();
  const [cancelJobRequestDialogOpen, openCancelJobRequestDialog, closeCancelJobRequestDialog] = useDialogState();
  const [archiveJobRequestDialogOpen, openArchiveJobRequestDialog, closeArchiveJobRequestDialog] = useDialogState();

  const paginatorResetRef = React.useRef<() => void>(() => {
    window.console.log("placeholder function");
  });

  const { snackbarAlert, openSnackbarAlert, closeSnackbarAlert } = useSnackbarAlert({
    autoHideDuration: 5000,
    vertical: "bottom",
    horizontal: "left"
  });
  const pageSessionState: MTJobRequestsPageSessionState | undefined = React.useMemo(
    () =>
      window.sessionStorage.getItem(pageSessionStateKeyName)
        ? JSON.parse(window.sessionStorage.getItem(pageSessionStateKeyName) as string)
        : undefined,
    [pageSessionStateKeyName]
  );
  const [rowsPerPage, setRowsPerPage] = useSessionStorage<number>(
    "mtJobRequests.rowsPerPage.1",
    pageSessionState?.rowsPerPage || 10
  );
  const [nextPageToken, setNextPageToken] = React.useState<string>();
  const [pageToken, setPageToken] = React.useState<string>();
  const [filterParams, setFilterParams] = React.useState<JobRequestFilterSearchParams>(
    pageSessionState?.filterParams || { group: DEFAULT_JOB_REQEST_GROUP }
  );

  // Keep session state of the page updated
  React.useEffect(() => {
    const newPageSessionState: MTJobRequestsPageSessionState = {
      rowsPerPage,
      filterParams
    };
    window.sessionStorage.setItem(pageSessionStateKeyName, JSON.stringify(newPageSessionState));
  }, [rowsPerPage, filterParams, pageSessionStateKeyName]);

  const {
    data: jobRequests,
    isLoading,
    error,
    refetch
  } = useQuery(
    [
      "listMTJobRequests",
      pageToken,
      rowsPerPage,
      filterParams?.requestType,
      filterParams?.status,
      filterParams?.requestId,
      filterParams?.group,
      filterParams?.name,
      origination
    ],
    {
      queryFn: () =>
        JobRequestsService.listJobRequests({
          origination,
          pageToken,
          pageSize: rowsPerPage,
          requestType: filterParams?.requestType,
          status: filterParams?.status,
          group: "INBOX",
          jobRequestId: filterParams?.requestId?.trim(),
          name: filterParams?.name
        }),
      onSuccess: collection => {
        setNextPageToken(collection.nextPageToken);
      },
      refetchInterval: 10000
    }
  );

  const requestedByCell = React.useCallback(
    (jobRequest: JobRequestSummary) =>
      `${jobRequest.requestorFirstName || ""} ${jobRequest.requestorLastName || ""} (${jobRequest.requestorEmail})`,
    []
  );

  const companyNameCell = React.useCallback(
    (jobRequest: JobRequestSummary) => (
      <Box maxWidth={200}>
        <Typography noWrap={true} title={jobRequest.companyName} variant="body2">
          {jobRequest.companyName || jobRequest.clientId}
        </Typography>
      </Box>
    ),
    []
  );

  const nameCell = React.useCallback(
    (jobRequest: JobRequestSummary) => (
      <Box maxWidth={300}>
        <Typography noWrap={true} title={jobRequest.name} variant="body2">
          <Link onClick={e => e.preventDefault()} href={getDetailsPagePath(jobRequest.id)}>
            {jobRequest.name}
          </Link>
        </Typography>
      </Box>
    ),
    [getDetailsPagePath]
  );

  const createTimeCell = React.useCallback((jobRequest: JobRequestSummary) => formatDate(jobRequest.createTime), []);

  const statusCell = React.useCallback(
    (jobRequest: JobRequestSummary) => <JobRequestStatusText status={jobRequest.status} />,
    []
  );

  const onRowClick = React.useCallback(
    (jobRequest: JobRequestSummary) => {
      history.push(getDetailsPagePath(jobRequest.id));
    },
    [getDetailsPagePath, history]
  );

  const onRowsPerPageChange = React.useCallback(
    (newRowsPerPage: number) => {
      setRowsPerPage(newRowsPerPage);
      paginatorResetRef.current();
      refetch();
    },
    [refetch, setRowsPerPage]
  );

  const onCancelJobRequestSuccess = React.useCallback(() => {
    refetch();
    closeCancelJobRequestDialog();
    openSnackbarAlert({ message: "Operation completed successfully", severity: "success" });
  }, [closeCancelJobRequestDialog, openSnackbarAlert, refetch]);

  const onDeleteJobRequestSuccess = React.useCallback(() => {
    refetch();
    closeDeleteJobRequestDialog();
    openSnackbarAlert({ message: "Operation completed successfully", severity: "success" });
  }, [closeDeleteJobRequestDialog, openSnackbarAlert, refetch]);

  const onArchiveJobRequestSuccess = React.useCallback(() => {
    refetch();
    closeArchiveJobRequestDialog();
    openSnackbarAlert({ message: "Operation completed successfully", severity: "success" });
  }, [closeArchiveJobRequestDialog, openSnackbarAlert, refetch]);

  const rowActions = React.useCallback(
    (jobRequest: JobRequestSummary): DataTableRowAction[] => [
      {
        title: "View Details",
        action: () => history.push(getDetailsPagePath(jobRequest.id))
      },
      {
        type: "DIVIDER",
        title: "divider1"
      },
      {
        title: "Cancel",
        disabled: ![JobRequestStatus.TRANSLATING, JobRequestStatus.CANCELLATION_REQUESTED].includes(jobRequest.status),
        action: () => {
          closeSnackbarAlert();
          setSelectedJobRequest(jobRequest);
          openCancelJobRequestDialog();
        },
        color: "error.main"
      },
      ...(!hideArchiveAction
        ? [
            {
              title: "Archive",
              disabled: !FINISHED_JOBREQUEST_STATUSES.includes(jobRequest.status),
              action: () => {
                closeSnackbarAlert();
                setSelectedJobRequest(jobRequest);
                openArchiveJobRequestDialog();
              }
            }
          ]
        : []),
      ...(!hideDeleteAction
        ? [
            {
              title: "Delete",
              disabled: ![...FINISHED_JOBREQUEST_STATUSES, JobRequestStatus.ARCHIVED].includes(jobRequest.status),
              action: () => {
                closeSnackbarAlert();
                setSelectedJobRequest(jobRequest);
                openDeleteJobRequestDialog();
              },
              color: "error.main"
            }
          ]
        : [])
    ],
    [
      hideArchiveAction,
      hideDeleteAction,
      history,
      getDetailsPagePath,
      closeSnackbarAlert,
      openCancelJobRequestDialog,
      openArchiveJobRequestDialog,
      openDeleteJobRequestDialog
    ]
  );

  const onFilterSearch = React.useCallback(
    (searchParams: JobRequestFilterSearchParams) => {
      setFilterParams(searchParams);
      paginatorResetRef.current();
      refetch();
    },
    [refetch]
  );

  return (
    <Stack spacing={1}>
      <Box>
        <JobRequestFilter
          initialValues={filterParams}
          onSearch={onFilterSearch}
          hideRequestTypeFilter={true}
          statuses={[
            JobRequestStatus.CREATED,
            JobRequestStatus.TRANSLATING,
            JobRequestStatus.FAILED,
            JobRequestStatus.SUBMITTED_TO_XTRF,
            JobRequestStatus.SUBMITTING_TO_XTRF,
            JobRequestStatus.CANCELLATION_REQUESTED,
            JobRequestStatus.CANCELLED,
            JobRequestStatus.COMPLETED,
            JobRequestStatus.ARCHIVED
          ]}
        />
      </Box>

      <DataTable
        columns={[
          {
            id: "name",
            title: "Name",
            cell: nameCell
          },
          {
            id: "requestedBy",
            title: "Requested By",
            cell: requestedByCell
          },
          ...(showCompanyName
            ? [
                {
                  id: "companyName",
                  title: "Company",
                  cell: companyNameCell
                }
              ]
            : []),
          {
            id: "createTime",
            title: "Created",
            cell: createTimeCell
          },
          {
            id: "status",
            title: "Status",
            cell: statusCell
          }
        ]}
        data={jobRequests?.items || []}
        rowKey={(jobRequest: JobRequestSummary) => jobRequest.id}
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        onRowClick={onRowClick}
        rowActions={rowActions}
        dense={true}
      />
      <TokenBasedPaginator
        nextPageToken={nextPageToken}
        rowsPerPage={rowsPerPage}
        onPageTokenChange={setPageToken}
        currentPageDataRows={jobRequests?.items.length}
        onRowsPerPageChange={onRowsPerPageChange}
        resetRef={paginatorResetRef}
      />
      {deleteJobRequestDialogOpen && selectedJobRequest && (
        <DeleteJobRequestDialog
          jobRequest={selectedJobRequest}
          onSuccess={onDeleteJobRequestSuccess}
          onClose={closeDeleteJobRequestDialog}
        />
      )}
      {cancelJobRequestDialogOpen && selectedJobRequest && (
        <CancelJobRequestDialog
          jobRequest={selectedJobRequest}
          onSuccess={onCancelJobRequestSuccess}
          onClose={closeCancelJobRequestDialog}
        />
      )}
      {archiveJobRequestDialogOpen && selectedJobRequest && (
        <ArchiveJobRequestDialog
          jobRequest={selectedJobRequest}
          onSuccess={onArchiveJobRequestSuccess}
          onClose={closeArchiveJobRequestDialog}
        />
      )}
      {snackbarAlert}
    </Stack>
  );
};

export { JobRequestList };
